// src/hooks/useTradingSocket.js
import { useEffect, useRef, useCallback } from 'react';
import { useSocket } from '../context/SocketContext';

/**
 * Hook for real-time trading market data via Socket.IO.
 * Mirrors useAuctionSocket pattern.
 *
 * @param {Object} options - Callback handlers
 * @param {Function} options.onJoined - Room joined confirmation
 * @param {Function} options.onOrderBookUpdate - Order book snapshot/diff
 * @param {Function} options.onTradeExecuted - New trade executed
 * @param {Function} options.onPriceUpdate - Token price tick
 * @param {Function} options.onError - Error events
 */
export default function useTradingSocket(options = {}) {
  const { socketService, isConnected, socket } = useSocket();
  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  useEffect(() => {
    if (!socket) return;

    const handlers = {
      joined_market_room: (data) => callbacksRef.current.onJoined?.(data),
      orderbook_update: (data) => callbacksRef.current.onOrderBookUpdate?.(data),
      trade_executed: (data) => callbacksRef.current.onTradeExecuted?.(data),
      price_update: (data) => callbacksRef.current.onPriceUpdate?.(data),
      error: (data) => callbacksRef.current.onError?.(data),
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
    };
  }, [socket]);

  const joinMarketRoom = useCallback(
    (tokenId) => {
      if (!socketService || !isConnected) return false;
      socket.emit('join_market_room', { token_id: tokenId });
      return true;
    },
    [socketService, isConnected, socket]
  );

  const leaveMarketRoom = useCallback(
    (tokenId) => {
      if (!socketService || !isConnected) return false;
      socket.emit('leave_market_room', { token_id: tokenId });
      return true;
    },
    [socketService, isConnected, socket]
  );

  return {
    isConnected,
    joinMarketRoom,
    leaveMarketRoom,
    socket,
  };
}
